/**
 * ============================================================================
 * A2A Process Manager - Orchestration with Status Control
 * ============================================================================
 * 
 * Manages multi-agent workflow orchestration with:
 * - Start/Stop/Pause controls
 * - 4-phase process tracking (Discovery, Execution, Validation, Completion)
 * - Detailed status messages for each agent action
 * - Progress tracking and error handling
 * - Reusable blueprint for all A2A systems on AppsOkay platform
 * 
 * BLUEPRINT FOR: HR Recruitment, School KPI, Teaching Aids, and future A2A systems
 */

/**
 * Process States
 */
export const ProcessState = {
  IDLE: 'idle',
  RUNNING: 'running',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ERROR: 'error',
  STOPPED: 'stopped'
};

/**
 * Process Phases (4 main phases)
 */
export const ProcessPhase = {
  PHASE_1_DISCOVERY: {
    id: 1,
    name: 'Discovery',
    description: 'Discovering capabilities and preparing resources',
    icon: '🔍',
    color: 'blue'
  },
  PHASE_2_EXECUTION: {
    id: 2,
    name: 'Execution',
    description: 'Executing agent tasks and processing data',
    icon: '⚙️',
    color: 'yellow'
  },
  PHASE_3_VALIDATION: {
    id: 3,
    name: 'Validation',
    description: 'Validating results and ensuring quality',
    icon: '✓',
    color: 'green'
  },
  PHASE_4_COMPLETION: {
    id: 4,
    name: 'Completion',
    description: 'Finalizing and saving results',
    icon: '🎉',
    color: 'purple'
  }
};

/**
 * Status Message Types
 */
export const MessageType = {
  INFO: 'info',
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  PROCESS: 'process'
};

/**
 * A2A Process Manager Class
 */
export class A2AProcessManager {
  constructor(options = {}) {
    this.state = ProcessState.IDLE;
    this.currentPhase = null;
    this.progress = 0;
    this.statusMessages = [];
    this.onStatusUpdate = options.onStatusUpdate || (() => {});
    this.onPhaseChange = options.onPhaseChange || (() => {});
    this.onComplete = options.onComplete || (() => {});
    this.onError = options.onError || (() => {});
    this.shouldStop = false;
    this.shouldPause = false;
    this.startTime = null;
    this.endTime = null;
    this.errors = [];
  }

  /**
   * Start the process
   */
  start() {
    if (this.state === ProcessState.RUNNING) {
      throw new Error('Process is already running');
    }

    this.state = ProcessState.RUNNING;
    this.shouldStop = false;
    this.shouldPause = false;
    this.startTime = Date.now();
    this.endTime = null;
    this.errors = [];
    this.progress = 0;
    this.statusMessages = [];
    
    this.addMessage('System', 'Process started', MessageType.INFO);
    this.updateStatus();
  }

  /**
   * Stop the process
   */
  stop() {
    if (this.state !== ProcessState.RUNNING && this.state !== ProcessState.PAUSED) {
      return;
    }

    this.shouldStop = true;
    this.state = ProcessState.STOPPED;
    this.endTime = Date.now();
    
    this.addMessage('System', 'Process stopped by user', MessageType.WARNING);
    this.updateStatus();
  }

  /**
   * Pause the process
   */
  pause() {
    if (this.state !== ProcessState.RUNNING) {
      return;
    }

    this.shouldPause = true;
    this.state = ProcessState.PAUSED;
    
    this.addMessage('System', 'Process paused', MessageType.INFO);
    this.updateStatus();
  }

  /**
   * Resume the process
   */
  resume() {
    if (this.state !== ProcessState.PAUSED) {
      return;
    }

    this.shouldPause = false;
    this.state = ProcessState.RUNNING;
    
    this.addMessage('System', 'Process resumed', MessageType.INFO);
    this.updateStatus();
  }

  /**
   * Check if should continue processing
   */
  shouldContinue() {
    return !this.shouldStop && !this.shouldPause && this.state === ProcessState.RUNNING;
  }

  /**
   * Set current phase
   */
  setPhase(phase) {
    this.currentPhase = phase;
    this.addMessage('System', `Entering ${phase.name} phase: ${phase.description}`, MessageType.INFO);
    this.onPhaseChange(phase);
    this.updateStatus();
  }

  /**
   * Update progress (0-100)
   */
  setProgress(progress) {
    this.progress = Math.min(100, Math.max(0, progress));
    this.updateStatus();
  }

  /**
   * Add status message
   */
  addMessage(agent, message, type = MessageType.INFO, metadata = {}) {
    const statusMessage = {
      id: `${Date.now()}-${Math.random()}`,
      agent,
      message,
      type,
      timestamp: new Date().toISOString(),
      timestampDisplay: new Date().toLocaleTimeString(),
      phase: this.currentPhase?.name || 'Initialization',
      metadata
    };

    this.statusMessages.push(statusMessage);
    
    // Keep only last 100 messages to avoid memory issues
    if (this.statusMessages.length > 100) {
      this.statusMessages = this.statusMessages.slice(-100);
    }

    this.updateStatus();
    return statusMessage;
  }

  /**
   * Add error
   */
  addError(agent, error, recoverable = false) {
    const errorInfo = {
      id: `${Date.now()}-${Math.random()}`,
      agent,
      error: error instanceof Error ? error.message : String(error),
      recoverable,
      timestamp: new Date().toISOString(),
      phase: this.currentPhase?.name || 'Unknown'
    };

    this.errors.push(errorInfo);
    this.addMessage(agent, `Error: ${errorInfo.error}`, MessageType.ERROR, { recoverable });

    if (!recoverable) {
      this.state = ProcessState.ERROR;
      this.endTime = Date.now();
      this.onError(errorInfo);
    }

    this.updateStatus();
  }

  /**
   * Mark process as completed
   */
  complete(result = null) {
    this.state = ProcessState.COMPLETED;
    this.endTime = Date.now();
    this.progress = 100;
    
    const duration = this.endTime - this.startTime;
    const durationText = `${Math.round(duration / 1000)}s`;
    
    this.addMessage('System', `Process completed successfully in ${durationText}`, MessageType.SUCCESS);
    this.onComplete(result);
    this.updateStatus();
  }

  /**
   * Update status (trigger callbacks)
   */
  updateStatus() {
    const status = this.getStatus();
    this.onStatusUpdate(status);
  }

  /**
   * Get current status
   */
  getStatus() {
    return {
      state: this.state,
      currentPhase: this.currentPhase,
      progress: this.progress,
      statusMessages: this.statusMessages,
      errors: this.errors,
      isRunning: this.state === ProcessState.RUNNING,
      isPaused: this.state === ProcessState.PAUSED,
      isCompleted: this.state === ProcessState.COMPLETED,
      isError: this.state === ProcessState.ERROR,
      isStopped: this.state === ProcessState.STOPPED,
      canStart: this.state === ProcessState.IDLE || this.state === ProcessState.COMPLETED || this.state === ProcessState.STOPPED || this.state === ProcessState.ERROR,
      canStop: this.state === ProcessState.RUNNING || this.state === ProcessState.PAUSED,
      canPause: this.state === ProcessState.RUNNING,
      canResume: this.state === ProcessState.PAUSED,
      startTime: this.startTime,
      endTime: this.endTime,
      duration: this.endTime ? this.endTime - this.startTime : (this.startTime ? Date.now() - this.startTime : 0)
    };
  }

  /**
   * Reset the process manager
   */
  reset() {
    this.state = ProcessState.IDLE;
    this.currentPhase = null;
    this.progress = 0;
    this.statusMessages = [];
    this.errors = [];
    this.shouldStop = false;
    this.shouldPause = false;
    this.startTime = null;
    this.endTime = null;
    
    this.updateStatus();
  }

  /**
   * Wait with pause/stop check
   */
  async wait(ms) {
    const startWait = Date.now();
    while (Date.now() - startWait < ms) {
      if (this.shouldStop) {
        throw new Error('Process stopped by user');
      }
      
      while (this.shouldPause) {
        await new Promise(resolve => setTimeout(resolve, 100));
        if (this.shouldStop) {
          throw new Error('Process stopped by user');
        }
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

/**
 * ============================================================================
 * A2A Workflow Orchestrator
 * ============================================================================
 * 
 * Orchestrates multi-agent workflows with 4-phase process management
 */
export class A2AWorkflowOrchestrator {
  constructor(a2aClient, processManager, options = {}) {
    this.client = a2aClient;
    this.processManager = processManager;
    this.options = options;
  }

  /**
   * Execute a task with automatic status tracking
   */
  async executeTask(taskName, params, options = {}) {
    const { agent = 'Agent', successMessage, errorMessage } = options;

    if (!this.processManager.shouldContinue()) {
      throw new Error('Process stopped or paused');
    }

    this.processManager.addMessage(agent, `Executing: ${taskName}`, MessageType.PROCESS);

    try {
      const result = await this.client.executeTask(taskName, params);
      
      const message = successMessage 
        ? (typeof successMessage === 'function' ? successMessage(result) : successMessage)
        : `✓ Task completed: ${taskName}`;
      
      this.processManager.addMessage(agent, message, MessageType.SUCCESS, { result });
      
      return result;
    } catch (error) {
      const message = errorMessage 
        ? (typeof errorMessage === 'function' ? errorMessage(error) : errorMessage)
        : `✗ Task failed: ${taskName}`;
      
      this.processManager.addError(agent, error, options.recoverable !== false);
      
      if (options.recoverable === false) {
        throw error;
      }
      
      return null;
    }
  }

  /**
   * Execute phase with progress tracking
   */
  async executePhase(phase, tasks, options = {}) {
    if (!this.processManager.shouldContinue()) {
      return;
    }

    this.processManager.setPhase(phase);
    
    const phaseProgress = options.startProgress || 0;
    const phaseRange = options.progressRange || 25; // Default: 25% per phase
    
    for (let i = 0; i < tasks.length; i++) {
      if (!this.processManager.shouldContinue()) {
        break;
      }

      const task = tasks[i];
      const taskProgress = phaseProgress + (i / tasks.length) * phaseRange;
      this.processManager.setProgress(taskProgress);

      if (typeof task === 'function') {
        await task(this);
      } else {
        await this.executeTask(task.name, task.params, task.options);
      }

      // Small delay for UI updates
      if (options.delayBetweenTasks !== false) {
        await this.processManager.wait(options.delayMs || 500);
      }
    }
    
    // Set progress to end of phase
    this.processManager.setProgress(phaseProgress + phaseRange);
  }
}

/**
 * ============================================================================
 * Export Utilities
 * ============================================================================
 */

export function createProcessManager(options = {}) {
  return new A2AProcessManager(options);
}

export function createWorkflowOrchestrator(a2aClient, processManager, options = {}) {
  return new A2AWorkflowOrchestrator(a2aClient, processManager, options);
}

/**
 * Format duration for display
 */
export function formatDuration(ms) {
  if (!ms) return '0s';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Get phase color class for Tailwind
 */
export function getPhaseColorClass(phase, type = 'bg') {
  const colors = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-500' },
    yellow: { bg: 'bg-yellow-500', text: 'text-yellow-600', border: 'border-yellow-500' },
    green: { bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-500' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-600', border: 'border-purple-500' }
  };
  
  return colors[phase?.color]?.[type] || colors.blue[type];
}

/**
 * Get message color class for Tailwind
 */
export function getMessageColorClass(messageType) {
  const colors = {
    info: 'text-gray-600',
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-yellow-600',
    process: 'text-blue-600'
  };
  
  return colors[messageType] || colors.info;
}

export default A2AProcessManager;

